import { effect, inject, Injectable, signal, WritableSignal } from "@angular/core";
import { BehaviorSubject, catchError, forkJoin, of, tap } from "rxjs";
import { PostService } from "./post.service";
import { ILocality, ITatbJob, PostState } from "./post.interface";
import { HrmMetadataApi } from "app/shared/service/metadata/hrm/hrm.api.service";
import { LocalityKeyEnum } from "../../recruit/candidate/constants";
import { STATUS_IN_DATABASE } from "app/shared/common/constants/Status.constant";
import { UtilCommon } from "app/shared/common/utils/UtilCommon";

@Injectable({ providedIn: "root" })
export class PostStore {
  #jobFamily: BehaviorSubject<ITatbJob[]> = new BehaviorSubject<ITatbJob[]>([]);
  #jobGroup: BehaviorSubject<ITatbJob[]> = new BehaviorSubject<ITatbJob[]>([]);
  #jobSkills: BehaviorSubject<ITatbJob[]> = new BehaviorSubject<ITatbJob[]>([]);
  #jobTypes: BehaviorSubject<ITatbJob[]> = new BehaviorSubject<ITatbJob[]>([]);
  #productGroups: BehaviorSubject<ITatbJob[]> = new BehaviorSubject<ITatbJob[]>([]);
  #channels: BehaviorSubject<ITatbJob[]> = new BehaviorSubject<ITatbJob[]>([]);
  #userChannels: BehaviorSubject<ITatbJob[]> = new BehaviorSubject<ITatbJob[]>([]);
  #provincies: BehaviorSubject<ILocality[]> = new BehaviorSubject<ILocality[]>([]);
  #jobTracks: BehaviorSubject<any[]> = new BehaviorSubject([]);
  #currentState: WritableSignal<PostState> = signal(null);

  #postService: PostService = inject(PostService);
  #hrmMetadataApi: HrmMetadataApi = inject(HrmMetadataApi);
  isStateStable = false;

  constructor() {
  }

  get provincies$() {
    return this.#provincies.asObservable();
  }

  getProvincies() {
    return this.#provincies.value;
  }

  get currentState(): PostState {
    return this.#currentState();
  }

  stateChanges(observer: (value: PostState) => void) {
    effect(() => observer(this.currentState));
  }

  public getPostResolver$() {
    if (this.isStateStable) return of(null);

    return this.#postService
      .getPostResolver()
      .pipe(
        catchError((error) => {
          this.isStateStable = false;
          return error;
        }),
        tap((data) => {
          this.isStateStable = true;

          UtilCommon.sortArray(data?.jobFamily);
          UtilCommon.sortArray(data?.jobGroup);
          UtilCommon.sortArray(data?.jobSkill);
          UtilCommon.sortArray(data?.jobType);
          UtilCommon.sortArray(data?.productGroup);
          UtilCommon.sortArray(data?.channel);
          UtilCommon.sortArray(data?.userChannel);
          UtilCommon.sortArray(data?.province);
          UtilCommon.sortArray(data?.jobTrack);
          this.#jobFamily.next(data?.jobFamily || []);
          this.#jobGroup.next(data?.jobGroup || []);
          this.#jobSkills.next(data?.jobSkill || []);
          this.#jobTypes.next(data?.jobType || []);
          this.#productGroups.next(data?.productGroup || []);
          this.#channels.next(data?.channel || []);
          this.#userChannels.next(data?.userChannel || []);
          this.#provincies.next(data?.province || []);
          this.#jobTracks.next(data?.jobTrack || []);

          this.#currentState.set({
            localities: this.#provincies?.value || null,
            jobFamily: this.#jobFamily?.value || null,
            jobGroup: this.#jobGroup?.value || null,
            jobSkills: this.#jobSkills?.value || null,
            jobTypes: this.#jobTypes?.value || null,
            productGroups: this.#productGroups?.value || null,
            jobTracks: this.#jobTracks.value || [],
            channels: this.#mappingChannelByUserChannel(
              this.#userChannels?.value,
              this.#channels?.value
            ),
            userChannels: this.#userChannels?.value || null,
          });
        }));
  }

  public getJobFamily$() {
    if (this.#jobFamily.value.length === 0) {
      return this.#postService
        .getAllJobFamily()
        .pipe(tap((data) => this.#jobFamily.next(data)));
    }
    return this.#jobFamily.asObservable();
  }

  public getJobGroup$() {
    if (this.#jobGroup.value.length === 0) {
      return this.#postService
        .getAllJobGroup()
        .pipe(tap((data) => this.#jobGroup.next(data)));
    }
    return this.#jobGroup.asObservable();
  }

  public getJobSkills$() {
    if (this.#jobSkills.value.length === 0) {
      return this.#postService
        .getAllSkill()
        .pipe(tap((data) => this.#jobSkills.next(data)));
    }
    return this.#jobSkills.asObservable();
  }

  public getJobTypes$() {
    if (this.#jobTypes.value.length === 0) {
      return this.#postService
        .getAllType()
        .pipe(tap((data) => this.#jobTypes.next(data)));
    }
    return this.#jobTypes.asObservable();
  }

  public getProductGroups$() {
    if (this.#productGroups.value.length === 0) {
      return this.#postService
        .getProductGroups()
        .pipe(tap((data) => this.#productGroups.next(data)));
    }
    return this.#productGroups.asObservable();
  }

  public getChannels$() {
    if (this.#channels.value.length === 0) {
      return this.#postService
        .getChannels()
        .pipe(tap((data) => this.#channels.next(data)));
    }
    return this.#channels.asObservable();
  }

  public getJobTracks$() {
    if (this.#jobTracks.value.length === 0) {
      return this.#hrmMetadataApi
        .getCareerPaths({ recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE }, isPersonalLevelFlg: { _eq: 1 } }
        )
        .pipe(
          tap((data) => this.#jobTracks.next(data)),
        );
    }
    return this.#jobTracks.asObservable();
  }

  public getProvincies$() {
    if (this.#provincies.value.length === 0) {
      return this.#hrmMetadataApi
        .getLocalitiesByWhere({ recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE }, pltm_locality_type: { code: { _eq: LocalityKeyEnum.PROVINCE } } })
        .pipe(
          tap((data) => this.#provincies.next(data)),
        );
    }
    return this.#provincies.asObservable();
  }

  public getUserChannels$() {
    if (this.#userChannels.value.length === 0) {
      return this.#postService
        .getUserChannels()
        .pipe(tap((data) => this.#userChannels.next(data)));
    }
    return this.#userChannels.asObservable();
  }
  public getAllJobDependencies$() {
    if (this.isStateStable) return of(null);
    return forkJoin([
      this.getJobFamily$(),
      this.getJobGroup$(),
      this.getJobSkills$(),
      this.getJobTypes$(),
      this.getProductGroups$(),
      this.getChannels$(),
      this.getUserChannels$(),
      this.getProvincies$(),
      this.getJobTracks$()
    ]).pipe(
      catchError((error) => {
        this.isStateStable = false;
        return error;
      }),
      tap(() => {
        this.isStateStable = true;
        this.#currentState.set({
          localities: this.#provincies?.value || null,
          jobFamily: this.#jobFamily?.value || null,
          jobGroup: this.#jobGroup?.value || null,
          jobSkills: this.#jobSkills?.value || null,
          jobTypes: this.#jobTypes?.value || null,
          productGroups: this.#productGroups?.value || null,
          jobTracks: this.#jobTracks.value || [],
          channels: this.#mappingChannelByUserChannel(
            this.#userChannels?.value,
            this.#channels?.value
          ),
          userChannels: this.#userChannels?.value || null,
        });
      })
    );
  }

  #mappingChannelByUserChannel = (
    userChannels: ITatbJob[],
    channels: ITatbJob[]
  ) => {
    return userChannels.filter((userChannel) =>
      channels?.some((channel) => channel?.code === userChannel?.code)
    );
  };

  filterJobGroupByJobFamily(jobFamilyId: number) {
    let listFiltered = this.#jobGroup.value.filter(
      (item) => item?.parentId === jobFamilyId
    );
    this.#currentState.set({ ...this.currentState, jobGroup: listFiltered });
    return listFiltered;
  }
}
